import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import { useAppMode } from '../../../contexts/AppModeContext';
import {
  ChevronLeft, ChevronRight, Plus, Sparkles, AlertTriangle,
  CheckCircle2, MapPin, DollarSign, Clock, User, Phone, Shield,
  Calendar, Building2, ExternalLink, Stethoscope, Activity, ScanLine,
  CalendarDays, List, LayoutGrid, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import CurrentTimeLine from './v2/CurrentTimeLine';
import { useCurrentTime } from './v2/useCurrentTime';
import RiskBreakdownPanel from './v2/RiskBreakdownPanel';
import ProviderColumnGrid from './v2/ProviderColumnGrid';
import RealCalendarMultiProvider from './v2/RealCalendarMultiProvider';
import AppointmentHoverDetails from './v2/AppointmentHoverDetails';
import NewAppointmentLightbox from './v2/NewAppointmentLightbox';
import {
  DEMO_OVERBOOKED_APT_IDS, DEMO_WEEK_OVERBOOK_BY_DATE, DEMO_CONTINGENCIES,
  DEMO_LIGHTBOX_PATIENTS, DEMO_LIGHTBOX_LOCATIONS, DEMO_LIGHTBOX_OPEN_SLOTS,
  DEMO_VISIT_TYPES_CATALOG,
} from '../../../lib/fixtures/schedulerV2Demo';
import { Layers, MoreVertical, Filter, X } from 'lucide-react';

/* Appointment visit type + status domain for filters (derived from APPOINTMENTS below) */
const VISIT_TYPES = ['New Patient', 'Follow-up', 'Sick Visit', 'Wellness', 'Walk-in', 'Annual Physical'];
const STATUSES = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'at_risk', label: 'At risk' },
  { id: 'flagged', label: 'Flagged' },
  { id: 'open', label: 'Open' },
];

/* ═══ PRACTICE MODE ═══ */
const LOCATIONS = [
  { id: 'main', name: 'Main Office', address: '100 Medical Pkwy, Austin TX', providers: ['dr-chen', 'dr-wilson', 'dr-roberts'] },
  { id: 'north', name: 'North Clinic', address: '450 Cedar Blvd, Austin TX', providers: ['dr-patel', 'dr-kim', 'dr-lee', 'dr-nguyen'] },
  { id: 'south', name: 'South Campus', address: '800 S Congress, Austin TX', providers: ['dr-foster', 'dr-reyes', 'dr-park'] },
  { id: 'east', name: 'East Branch', address: '220 E Riverside, Austin TX', providers: ['dr-thompson', 'dr-wright', 'dr-garcia', 'dr-martinez'] },
];

const ALL_PROVIDERS = [
  { id: 'dr-chen', name: 'Dr. Chen', color: '#3b82f6', specialty: 'Internal Medicine', location: 'main' },
  { id: 'dr-wilson', name: 'Dr. Wilson', color: '#8b5cf6', specialty: 'Family Medicine', location: 'main' },
  { id: 'dr-roberts', name: 'Dr. Roberts', color: '#f59e0b', specialty: 'Pediatrics', location: 'main' },
  { id: 'dr-patel', name: 'Dr. Patel', color: '#22c55e', specialty: 'Internal Medicine', location: 'north' },
  { id: 'dr-kim', name: 'Dr. Kim', color: '#ef4444', specialty: 'Cardiology', location: 'north' },
  { id: 'dr-lee', name: 'Dr. Lee', color: '#06b6d4', specialty: 'Family Medicine', location: 'north' },
  { id: 'dr-nguyen', name: 'Dr. Nguyen', color: '#ec4899', specialty: 'OB/GYN', location: 'north' },
  { id: 'dr-foster', name: 'Dr. Foster', color: '#14b8a6', specialty: 'Pulmonology', location: 'south' },
  { id: 'dr-reyes', name: 'Dr. Reyes', color: '#a855f7', specialty: 'Nephrology', location: 'south' },
  { id: 'dr-park', name: 'Dr. Park', color: '#f97316', specialty: 'Psychiatry', location: 'south' },
  { id: 'dr-thompson', name: 'Dr. Thompson', color: '#64748b', specialty: 'Internal Medicine', location: 'east' },
  { id: 'dr-wright', name: 'Dr. Wright', color: '#eab308', specialty: 'Dermatology', location: 'east' },
  { id: 'dr-garcia', name: 'Dr. Garcia', color: '#6366f1', specialty: 'Orthopedics', location: 'east' },
  { id: 'dr-martinez', name: 'Dr. Martinez', color: '#dc2626', specialty: 'ENT', location: 'east' },
];

const ENTERPRISE_ROLES = [
  { id: 'regional_ops', label: 'Regional Ops Manager', desc: 'All locations, capacity & revenue focus', icon: Building2 },
  { id: 'practice_admin', label: 'Practice Administrator', desc: 'Single location, full operational control', icon: Shield },
  { id: 'scheduling_coord', label: 'Scheduling Coordinator', desc: 'Cross-location booking & waitlist', icon: Calendar },
  { id: 'front_desk_lead', label: 'Front Desk Lead', desc: 'Patient flow & check-in at one site', icon: User },
  { id: 'call_center', label: 'Central Scheduling Agent', desc: 'Phone bookings across all locations', icon: Phone },
];

/* Patient details for lightbox */
const PATIENT_DETAILS = {
  a1: { name: 'John Smith', dob: '03/15/1962', mrn: 'MRN-10042', phone: '(555) 123-4567', insurance: 'Aetna PPO', insStatus: 'Active', lastVisit: '03/28/2026', problems: ['Type 2 Diabetes', 'Hypertension'], meds: ['Metformin 1000mg', 'Lisinopril 20mg'], allergies: ['Penicillin'] },
  a2: { name: 'Maria Garcia', dob: '07/22/1985', mrn: 'MRN-20198', phone: '(555) 234-5678', insurance: 'Blue Cross', insStatus: 'Active', lastVisit: 'New Patient', problems: [], meds: [], allergies: ['Latex'] },
  a3: { name: 'Robert Johnson', dob: '01/10/1958', mrn: 'MRN-30445', phone: '(555) 345-6789', insurance: 'Medicare', insStatus: 'Inactive', lastVisit: '02/15/2026', problems: ['COPD', 'CHF', 'AFib'], meds: ['Metoprolol 50mg', 'Eliquis 5mg', 'Spiriva'], allergies: ['Codeine', 'Sulfa'] },
  a4: { name: 'Emily Davis', dob: '11/30/1978', mrn: 'MRN-40112', phone: '(555) 456-7890', insurance: 'Aetna HMO', insStatus: 'Active', lastVisit: '01/20/2026', problems: ['Hypothyroidism'], meds: ['Levothyroxine 75mcg'], allergies: [] },
  a5: { name: 'James Taylor', dob: '09/08/1970', mrn: 'MRN-50287', phone: '(555) 567-8901', insurance: 'United', insStatus: 'Active', lastVisit: '11/02/2025', problems: ['Obesity', 'Pre-diabetes'], meds: [], allergies: ['Iodine'] },
  a7: { name: 'Sarah Williams', dob: '05/14/1990', mrn: 'MRN-60331', phone: '(555) 678-9012', insurance: 'Cigna', insStatus: 'Active', lastVisit: '03/10/2026', problems: ['Anxiety', 'IBS'], meds: ['Sertraline 100mg'], allergies: [] },
  a8: { name: 'David Wilson', dob: '12/03/1982', mrn: 'MRN-70445', phone: '(555) 789-0123', insurance: 'Aetna PPO', insStatus: 'Active', lastVisit: 'New Patient', problems: [], meds: [], allergies: ['Amoxicillin'] },
  a9: { name: 'Lisa Brown', dob: '04/19/1975', mrn: 'MRN-80129', phone: '(555) 890-1234', insurance: 'Blue Cross', insStatus: 'Active', lastVisit: '02/28/2026', problems: ['Migraine', 'Asthma'], meds: ['Sumatriptan PRN', 'Albuterol MDI'], allergies: [] },
};

const APPOINTMENTS = [
  { id: 'a1', time: '9:00', endTime: '9:30', patient: 'John Smith', type: 'Follow-up', provider: 'dr-chen', status: 'confirmed', noShow: 5, copay: null, room: 'Room 1', location: 'main' },
  { id: 'a2', time: '9:30', endTime: '10:15', patient: 'Maria Garcia', type: 'New Patient', provider: 'dr-wilson', status: 'confirmed', noShow: 12, copay: null, room: 'Room 2', location: 'main' },
  { id: 'a3', time: '10:00', endTime: '10:20', patient: 'Robert Johnson', type: 'Sick Visit', provider: 'dr-chen', status: 'flagged', noShow: 8, copay: null, room: null, location: 'main' },
  { id: 'a3b', time: '10:00', endTime: '10:20', patient: 'Amy Nguyen', type: 'Walk-in', provider: 'dr-chen', status: 'scheduled', noShow: 14, copay: null, room: null, location: 'main' },
  { id: 'a4', time: '10:30', endTime: '11:00', patient: 'Emily Davis', type: 'Follow-up', provider: 'dr-chen', status: 'scheduled', noShow: 22, copay: 20, room: null, location: 'main' },
  { id: 'a5', time: '11:00', endTime: '11:45', patient: 'James Taylor', type: 'Wellness', provider: 'dr-wilson', status: 'at_risk', noShow: 35, copay: null, room: null, location: 'main' },
  { id: 'a6', time: '11:30', endTime: '12:00', patient: null, type: null, provider: null, status: 'open', noShow: null, copay: null, room: null, location: 'main' },
  { id: 'a7', time: '13:00', endTime: '13:30', patient: 'Sarah Williams', type: 'Follow-up', provider: 'dr-chen', status: 'scheduled', noShow: 15, copay: null, room: null, location: 'main' },
  { id: 'a8', time: '14:00', endTime: '14:45', patient: 'David Wilson', type: 'New Patient', provider: 'dr-wilson', status: 'at_risk', noShow: 42, copay: null, room: null, location: 'main' },
  { id: 'a9', time: '15:00', endTime: '15:20', patient: 'Lisa Brown', type: 'Sick Visit', provider: 'dr-chen', status: 'scheduled', noShow: 10, copay: null, room: null, location: 'main' },
];

const STATUS_STYLE = {
  confirmed: { bg: '#f0fdf4', border: '#22c55e', label: 'AI confirmed' },
  scheduled: { bg: '#f0f0f0', border: '#d1d5db', label: null },
  flagged: { bg: '#fef2f2', border: '#ef4444', label: 'Insurance flagged' },
  at_risk: { bg: '#fef2f2', border: '#ef4444', label: null },
  in_exam: { bg: '#eff6ff', border: '#3b82f6', label: 'In exam' },
  checked_out: { bg: '#f9f9f9', border: '#d1d5db', label: 'Done' },
  open: { bg: 'transparent', border: '#d1d5db', label: null },
};

const HOURS = ['8:00', '9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 17; // last hour row is 16:00 → 17:00
const HOUR_PX = 56; // matches min-h-[56px] on hour rows
const GUTTER_WIDTH = 64; // matches w-16 (16 * 4 = 64)
const formatHour = (h) => { const n = parseInt(h); return n >= 13 ? `${n-12}:00 PM` : n === 12 ? '12:00 PM' : `${h} AM`; };

const WEEK_DATA = [
  { day: 'Mon', date: 14, count: 7, revenue: '$1,820', status: 'mixed' },
  { day: 'Tue', date: 15, count: 9, revenue: '$2,340', status: 'mixed' },
  { day: 'Wed', date: 16, count: 6, revenue: '$1,560', status: 'good' },
  { day: 'Thu', date: 17, count: 8, revenue: '$2,840', status: 'mixed', isToday: true },
  { day: 'Fri', date: 18, count: 5, revenue: '$1,300', status: 'risk' },
  { day: 'Sat', date: 19, count: 2, revenue: '$520', status: 'good' },
  { day: 'Sun', date: 20, count: 0, revenue: '$0', status: 'good' },
];

const MONTH_WEEKS = [
  [null, 1, 2, 3, 4, 5, 6], [7, 8, 9, 10, 11, 12, 13],
  [14, 15, 16, 17, 18, 19, 20], [21, 22, 23, 24, 25, 26, 27],
  [28, 29, 30, null, null, null, null],
];

/* ═══ COMPONENT ═══ */
const SchedulerCalendar = ({ onBook }) => {
  const navigate = useNavigate();
  const { isDemo } = useAppMode();
  const [calView, setCalView] = useState('day');
  const [practiceMode, setPracticeMode] = useState('solo'); // solo | enterprise
  const [enterpriseRole, setEnterpriseRole] = useState('regional_ops');
  const [locationFilter, setLocationFilter] = useState(LOCATIONS.map(l => l.id));
  const [providerFilter, setProviderFilter] = useState(ALL_PROVIDERS.filter(p => p.location === 'main').map(p => p.id));
  const [visitTypeFilter, setVisitTypeFilter] = useState(VISIT_TYPES); // default: all
  const [statusFilter, setStatusFilter] = useState(STATUSES.map((s) => s.id)); // default: all
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [lightboxApt, setLightboxApt] = useState(null);
  const [overbookApt, setOverbookApt] = useState(null);
  const [riskApt, setRiskApt] = useState(null);
  const [ctxMenuId, setCtxMenuId] = useState(null);
  const [contingencies, setContingencies] = useState(DEMO_CONTINGENCIES);
  const [newApptOpen, setNewApptOpen] = useState(false);
  const [newApptPrefill, setNewApptPrefill] = useState(null);
  const [selectedDate, setSelectedDate] = useState(17); // day-of-month focus
  const [listView, setListView] = useState(false); // toggle for list/grid view
  const [appointments, setAppointments] = useState(APPOINTMENTS);
  // Edit 2: current-time indicator + smart initial scroll position.
  // The demo calendar is always pinned to "today" so we enable the red
  // line by default. Real-mode calendar is empty-state (see below).
  const now = useCurrentTime(60000);
  const dayScrollRef = useRef(null);
  const weekScrollRef = useRef(null);
  const isViewingToday = true;

  useEffect(() => {
    // Run once on mount per view. Scrolls current time into upper third.
    const hours = now.getHours() + now.getMinutes() / 60;
    const clamped = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR, hours));
    const y = (clamped - DAY_START_HOUR) * HOUR_PX;
    const anchor = Math.max(0, y - (HOUR_PX * 2)); // upper-third: ~2h above
    if (calView === 'day' && dayScrollRef.current) {
      dayScrollRef.current.scrollTop = isViewingToday ? anchor : 0;
    }
    if (calView === 'week' && weekScrollRef.current) {
      weekScrollRef.current.scrollTop = isViewingToday ? anchor : 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calView]);

  // Real mode: render the live multi-provider calendar backed by the
  // scheduler API. Falls through to the demo UI below only when isDemo.
  if (!isDemo) {
    return <RealCalendarMultiProvider onBook={onBook} />;
  }

  // Derive visible providers based on mode
  const visibleProviders = practiceMode === 'solo'
    ? ALL_PROVIDERS.filter(p => p.location === 'main')
    : ALL_PROVIDERS.filter(p => locationFilter.includes(p.location));

  const toggleProvider = (id) => {
    setProviderFilter(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };
  const toggleLocation = (id) => {
    setLocationFilter(prev => {
      const next = prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id];
      // Also update provider filter
      const locProviders = ALL_PROVIDERS.filter(p => next.includes(p.location)).map(p => p.id);
      setProviderFilter(locProviders);
      return next;
    });
  };

  const handleModeSwitch = (mode) => {
    setPracticeMode(mode);
    if (mode === 'solo') {
      setLocationFilter(['main']);
      setProviderFilter(ALL_PROVIDERS.filter(p => p.location === 'main').map(p => p.id));
    } else {
      setLocationFilter(LOCATIONS.map(l => l.id));
      setProviderFilter(ALL_PROVIDERS.map(p => p.id));
    }
  };

  const filteredApts = appointments
    .filter((a) => !a.provider || providerFilter.includes(a.provider))
    .filter((a) => visitTypeFilter.length === 0 || (a.type && visitTypeFilter.includes(a.type)))
    .filter((a) => statusFilter.length === 0 || (a.status && statusFilter.includes(a.status)));
  const todayOBCount = filteredApts.filter(a => DEMO_OVERBOOKED_APT_IDS.has(a.id)).length;
  const today = new Date().getDate();

  const openLightbox = (apt) => {
    if (apt.status === 'open') return;
    setLightboxApt(apt);
  };

  return (
    <div className="space-y-4" data-testid="scheduler-calendar">
      {/* ═══ MODE TOGGLE + FILTERS ═══ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedDate((d) => Math.max(1, d - 1))} data-testid="cal-prev-day"><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <span className="text-sm font-semibold text-slate-800 min-w-[160px] text-center" data-testid="cal-date-label">
              {calView === 'day' ? `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(2026, 3, selectedDate).getDay()]}, April ${selectedDate}` : calView === 'week' ? 'Apr 14 – 20, 2026' : 'April 2026'}
            </span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedDate((d) => Math.min(30, d + 1))} data-testid="cal-next-day"><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
          <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setSelectedDate(17)} data-testid="cal-today-btn">Today</Button>
        </div>

        <div className="flex items-center gap-2">
          {/* Practice mode toggle */}
          <div className="flex rounded-lg border overflow-hidden mr-2" data-testid="practice-mode-toggle">
            <button onClick={() => handleModeSwitch('solo')}
              className={cn("px-3 py-1.5 text-[10px] font-medium transition-colors", practiceMode === 'solo' ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
              style={{ border: 'none', cursor: 'pointer' }} data-testid="mode-solo">Solo / Small</button>
            <button onClick={() => handleModeSwitch('enterprise')}
              className={cn("px-3 py-1.5 text-[10px] font-medium transition-colors gap-1 flex items-center", practiceMode === 'enterprise' ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
              style={{ border: 'none', cursor: 'pointer' }} data-testid="mode-enterprise">
              <Building2 className="h-2.5 w-2.5" /> Enterprise
            </button>
          </div>

          {/* View toggles */}
          <div className="flex rounded-lg border overflow-hidden" data-testid="cal-view-toggle">
            {['day', 'week', 'month'].map(v => (
              <button key={v} onClick={() => setCalView(v)}
                className={cn("px-3 py-1.5 text-[10px] font-medium transition-colors", calView === v ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
                style={{ border: 'none', cursor: 'pointer' }} data-testid={`cal-view-${v}`}
              >{v.charAt(0).toUpperCase() + v.slice(1)}</button>
            ))}
          </div>

          {/* Grid / List view toggle */}
          <div className="flex rounded-lg border overflow-hidden" data-testid="cal-listmode-toggle">
            <button onClick={() => setListView(false)}
              className={cn("px-2.5 py-1.5 text-[10px] font-medium transition-colors inline-flex items-center gap-1", !listView ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
              style={{ border: 'none', cursor: 'pointer' }} data-testid="cal-view-grid" title="Grid view">
              <LayoutGrid className="h-3 w-3" />
            </button>
            <button onClick={() => setListView(true)}
              className={cn("px-2.5 py-1.5 text-[10px] font-medium transition-colors inline-flex items-center gap-1", listView ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
              style={{ border: 'none', cursor: 'pointer' }} data-testid="cal-view-list" title="List view">
              <List className="h-3 w-3" />
            </button>
          </div>
          {/* Filters button — opens collapsible universal filter row */}
          <Button
            variant={filtersOpen ? 'default' : 'outline'}
            size="sm"
            className={cn('h-7 text-[10px] gap-1', filtersOpen && 'bg-[#111] hover:bg-[#222]')}
            onClick={() => setFiltersOpen((v) => !v)}
            data-testid="cal-filters-toggle"
          >
            <Filter className="h-3 w-3" />
            Filters
            {(visitTypeFilter.length < VISIT_TYPES.length || statusFilter.length < STATUSES.length) && (
              <span className="ml-1 text-[9px] bg-amber-100 text-amber-800 rounded-full px-1.5 leading-snug">active</span>
            )}
          </Button>
        </div>
      </div>

      {/* ═══ UNIVERSAL FILTERS (location · visit type · status) ═══ */}
      {filtersOpen && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2.5" data-testid="cal-filters-panel">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase font-semibold tracking-wide text-slate-500 inline-flex items-center gap-1">
              <Filter className="h-3 w-3" /> Filters
            </p>
            <button
              onClick={() => {
                setVisitTypeFilter(VISIT_TYPES);
                setStatusFilter(STATUSES.map((s) => s.id));
                setLocationFilter(LOCATIONS.map((l) => l.id));
                if (practiceMode === 'solo') {
                  setProviderFilter(ALL_PROVIDERS.filter((p) => p.location === 'main').map((p) => p.id));
                } else {
                  setProviderFilter(ALL_PROVIDERS.map((p) => p.id));
                }
              }}
              className="text-[9px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              data-testid="cal-filters-reset"
            >
              <X className="h-2.5 w-2.5" /> Reset all
            </button>
          </div>

          {/* Locations — now available in solo mode too */}
          <div className="flex items-start gap-2.5">
            <span className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide pt-0.5 w-14 shrink-0">Location</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {LOCATIONS.map((loc) => {
                const on = locationFilter.includes(loc.id);
                return (
                  <button
                    key={loc.id}
                    onClick={() => toggleLocation(loc.id)}
                    className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors border',
                      on ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100'
                    )}
                    style={{ cursor: 'pointer' }}
                    data-testid={`filter-loc-${loc.id}`}
                  >
                    <Building2 className="h-2.5 w-2.5" /> {loc.name}
                    <span className="text-[8px] opacity-70">({loc.providers.length})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Visit types */}
          <div className="flex items-start gap-2.5">
            <span className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide pt-0.5 w-14 shrink-0">Type</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setVisitTypeFilter(VISIT_TYPES)}
                className="text-[9px] text-slate-500 hover:text-slate-900"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid="filter-vt-all"
              >All</button>
              <span className="text-[9px] text-slate-300">·</span>
              <button
                onClick={() => setVisitTypeFilter([])}
                className="text-[9px] text-slate-500 hover:text-slate-900 mr-1"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid="filter-vt-none"
              >None</button>
              {VISIT_TYPES.map((vt) => {
                const on = visitTypeFilter.includes(vt);
                return (
                  <button
                    key={vt}
                    onClick={() => setVisitTypeFilter((prev) => (prev.includes(vt) ? prev.filter((x) => x !== vt) : [...prev, vt]))}
                    className={cn('px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors border',
                      on ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100'
                    )}
                    style={{ cursor: 'pointer' }}
                    data-testid={`filter-vt-${vt.replace(/\W+/g, '-').toLowerCase()}`}
                  >{vt}</button>
                );
              })}
            </div>
          </div>

          {/* Statuses */}
          <div className="flex items-start gap-2.5">
            <span className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide pt-0.5 w-14 shrink-0">Status</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setStatusFilter(STATUSES.map((s) => s.id))}
                className="text-[9px] text-slate-500 hover:text-slate-900"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid="filter-status-all"
              >All</button>
              <span className="text-[9px] text-slate-300">·</span>
              <button
                onClick={() => setStatusFilter([])}
                className="text-[9px] text-slate-500 hover:text-slate-900 mr-1"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid="filter-status-none"
              >None</button>
              {STATUSES.map((s) => {
                const on = statusFilter.includes(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => setStatusFilter((prev) => (prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id]))}
                    className={cn('px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors border',
                      on ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100'
                    )}
                    style={{ cursor: 'pointer' }}
                    data-testid={`filter-status-${s.id}`}
                  >{s.label}</button>
                );
              })}
            </div>
          </div>
          <p className="text-[9px] text-slate-400 pt-1">
            Showing <b>{filteredApts.length}</b> of {appointments.length} appointments · {visibleProviders.filter((p) => providerFilter.includes(p.id)).length} providers
          </p>
        </div>
      )}
      {DEMO_OVERBOOKED_APT_IDS.size > 0 && (
        <div
          className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2"
          data-testid="ob-legend"
        >
          <div className="flex items-center gap-1.5 shrink-0">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-[11px] font-semibold text-amber-800">Overbooking key</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap text-[10px] text-slate-700">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-4 w-4 rounded border-l-[3px]"
                style={{ borderLeftColor: '#f59e0b', background: '#fffbeb' }}
                aria-hidden
              />
              <span>Amber card + <b>OB</b> badge = overbooked appointment (click to review)</span>
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="ob-legend-week-key">
              <span className="inline-block px-1.5 py-0.5 rounded-full text-[8px] font-semibold bg-red-50 text-red-700">4/3 OB</span>
              <span>= over daily limit</span>
              <span className="inline-block px-1.5 py-0.5 rounded-full text-[8px] font-semibold bg-amber-50 text-amber-700 ml-2">3/3 OB</span>
              <span>= at limit</span>
            </span>
          </div>
          <div
            className="ml-auto inline-flex items-center gap-1 text-[10px] text-amber-700 bg-white border border-amber-200 rounded-md px-2 py-1"
            data-testid="ob-today-count"
          >
            <b>{todayOBCount}</b> overbooked today
          </div>
        </div>
      )}

      {/* ═══ ENTERPRISE ROLE SELECTOR (locations now live in universal Filters panel) ═══ */}
      {practiceMode === 'enterprise' && (
        <div className="space-y-2" data-testid="enterprise-controls">
          {/* Role selector */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            {ENTERPRISE_ROLES.map(r => (
              <button key={r.id} onClick={() => setEnterpriseRole(r.id)}
                className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap transition-colors",
                  enterpriseRole === r.id ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`role-${r.id}`}>
                <r.icon className="h-3 w-3" /> {r.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Provider filter line removed — now lives in the Providers card under the mini calendar (user request) */}

      {/* ═══ DAY VIEW ═══ */}
      {calView === 'day' && (
        <div className="flex gap-4">
          {/* Mini Calendar + Providers card + Summary cards (left rail) */}
          <div className="w-56 space-y-3 shrink-0" data-testid="cal-left-rail">
            {/* Mini calendar */}
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="mini-calendar">
              <div className="flex items-center justify-between mb-2">
                <Button variant="ghost" size="icon" className="h-5 w-5"><ChevronLeft className="h-3 w-3" /></Button>
                <span className="text-[11px] font-semibold text-slate-800">April 2026</span>
                <Button variant="ghost" size="icon" className="h-5 w-5"><ChevronRight className="h-3 w-3" /></Button>
              </div>
              <div className="grid grid-cols-7 gap-0">
                {['S','M','T','W','T','F','S'].map((d,i) => (
                  <div key={i} className="text-center text-[8px] text-slate-400 font-semibold py-0.5">{d}</div>
                ))}
                {[null, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30].map((d, i) => {
                  const isSelected = d === selectedDate;
                  const isToday = d === 17;
                  const hasApts = d && d >= 14 && d <= 20;
                  return (
                    <button
                      key={i}
                      disabled={!d}
                      onClick={() => {
                        if (!d) return;
                        setSelectedDate(d);
                        setCalView('day');
                      }}
                      className={cn("text-center py-1 text-[10px] rounded-md transition-colors",
                        isSelected ? "bg-[#111] text-white font-bold" :
                        isToday ? "bg-slate-100 text-slate-900 font-semibold ring-1 ring-slate-300" :
                        hasApts ? "text-slate-700 font-medium hover:bg-slate-100" :
                        d ? "text-slate-400 hover:bg-slate-50" : ""
                      )}
                      style={{ border: 'none', cursor: d ? 'pointer' : 'default', background: isSelected ? '#111' : undefined }}
                      data-testid={d ? `mini-cal-day-${d}` : undefined}
                    >
                      {d || ''}
                      {hasApts && !isSelected && <div className={cn("h-0.5 w-0.5 rounded-full mx-auto mt-0", isToday ? 'bg-slate-600' : 'bg-slate-400')} />}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Providers card (replaces the top-bar Provider filter pills) */}
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="cal-providers-card">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide inline-flex items-center gap-1">
                  <Users className="h-3 w-3" /> Providers
                </p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setProviderFilter(visibleProviders.map(p => p.id))}
                    className="text-[9px] text-slate-500 hover:text-slate-900"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    data-testid="providers-card-all"
                  >All</button>
                  <span className="text-[9px] text-slate-300">·</span>
                  <button
                    onClick={() => setProviderFilter([])}
                    className="text-[9px] text-slate-500 hover:text-slate-900"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    data-testid="providers-card-none"
                  >None</button>
                </div>
              </div>
              <div className="space-y-1">
                {visibleProviders.map((p) => {
                  const on = providerFilter.includes(p.id);
                  const loc = LOCATIONS.find(l => l.id === p.location);
                  return (
                    <label
                      key={p.id}
                      className={cn("flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors",
                        on ? "bg-slate-50 hover:bg-slate-100" : "hover:bg-slate-50"
                      )}
                      data-testid={`cal-provider-row-${p.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleProvider(p.id)}
                        className="h-3 w-3 rounded border-slate-300 accent-[#111]"
                        data-testid={`cal-provider-toggle-${p.id}`}
                      />
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: on ? p.color : '#d1d5db' }} />
                      <span className={cn("text-[11px] font-medium truncate flex-1", on ? 'text-slate-800' : 'text-slate-400')}>{p.name}</span>
                      {practiceMode === 'enterprise' && loc && (
                        <span className="text-[8px] text-slate-400 uppercase">{loc.name.split(' ')[0]}</span>
                      )}
                    </label>
                  );
                })}
                {visibleProviders.length === 0 && (
                  <p className="text-[10px] text-slate-400 italic px-2 py-2">No providers available for the selected locations.</p>
                )}
              </div>
            </div>
            {/* Summary cards (moved from right rail — user request) */}
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="cal-financial-summary">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Financial summary</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-[11px]"><span className="text-slate-500">Today&apos;s potential</span><span className="font-semibold text-slate-800">$2,840</span></div>
                <div className="flex justify-between text-[11px]"><span className="text-slate-500">Confirmed</span><span className="font-semibold text-emerald-600">$1,650</span></div>
                <div className="flex justify-between text-[11px]"><span className="text-slate-500">At risk</span><span className="font-semibold text-amber-600">$600</span></div>
                <div className="flex justify-between text-[11px]"><span className="text-slate-500">Lost (no-shows)</span><span className="font-semibold text-red-600">$0</span></div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="cal-noshow-card">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">No-show management</p>
              <p className="text-[11px] text-slate-600"><span className="font-semibold text-red-600">2</span> high-risk patients</p>
              <p className="text-[11px] text-slate-600">Echo sent <span className="font-semibold">7</span> reminders</p>
              <p className="text-[11px] text-emerald-600">Est. savings: <span className="font-semibold">$400</span></p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="cal-slot-card">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Slot optimization</p>
              <p className="text-[11px] text-slate-600"><span className="font-semibold">1</span> open slot</p>
              <p className="text-[11px] text-slate-600">Echo has <span className="font-semibold text-slate-800">3</span> matches</p>
              <Button size="sm" className="w-full h-7 text-[10px] bg-[#111] hover:bg-[#222] mt-1" onClick={() => toast.success('Echo AI filling all open slots...')}>Fill all</Button>
            </div>
          </div>

          <div
            ref={dayScrollRef}
            className="flex-1 rounded-xl border border-slate-200 bg-white overflow-y-auto relative"
            style={{ maxHeight: 620 }}
            data-testid="day-view"
          >
            {/* LIST VIEW for Day (compact list of appointments) */}
            {listView && (
              <div className="p-4 space-y-2" data-testid="day-list-view">
                <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide mb-2 flex items-center justify-between">
                  <span>Schedule · {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(2026, 3, selectedDate).getDay()]}, April {selectedDate}</span>
                  <span>{filteredApts.filter(a => a.status !== 'open').length} appointments</span>
                </div>
                {filteredApts.filter(a => a.status !== 'open').length === 0 && (
                  <p className="text-[11px] text-slate-400 italic py-4 text-center">No appointments for this day.</p>
                )}
                {filteredApts
                  .filter((a) => a.status !== 'open')
                  .sort((a, b) => {
                    const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                    return toMin(a.time) - toMin(b.time);
                  })
                  .map((apt) => {
                    const st = STATUS_STYLE[apt.status] || STATUS_STYLE.scheduled;
                    const isOB = DEMO_OVERBOOKED_APT_IDS.has(apt.id);
                    const provObj = ALL_PROVIDERS.find(p => p.id === apt.provider);
                    return (
                      <button
                        key={apt.id}
                        onClick={() => openLightbox(apt)}
                        className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors"
                        style={{ cursor: 'pointer', borderLeft: `3px solid ${isOB ? '#f59e0b' : st.border}` }}
                        data-testid={`day-list-row-${apt.id}`}
                      >
                        <span className="text-[11px] font-medium text-slate-500 w-20 shrink-0">{formatHour(apt.time)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-[12px] font-semibold text-slate-800 truncate">{apt.patient}</p>
                            {isOB && <Badge className="text-[8px] border-0 bg-amber-50 text-amber-700">OB</Badge>}
                            {apt.copay && <Badge variant="outline" className="text-[8px] border-amber-200 text-amber-600">${apt.copay} due</Badge>}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {apt.type} · <span className="inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ background: provObj?.color }} /> {provObj?.name || apt.provider}
                            {apt.room && <> · {apt.room}</>}
                          </div>
                        </div>
                        {st.label && (
                          <span className="text-[9px] text-slate-500 whitespace-nowrap hidden md:inline">
                            {st.label}
                          </span>
                        )}
                        {apt.noShow > 30 && (
                          <span className="text-[9px] text-red-600 whitespace-nowrap">⚠ {apt.noShow}%</span>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
            {/* Edit 2: persistent current-time red line (today only) */}
            {!listView && isViewingToday && (
              <CurrentTimeLine
                dayStartHour={DAY_START_HOUR}
                dayEndHour={DAY_END_HOUR}
                gutterWidth={GUTTER_WIDTH}
                hourPx={HOUR_PX}
                mode="day"
                testId="calendar-current-time-day"
              />
            )}
            {/* ═══ MULTI-PROVIDER GRID — when 2+ providers selected (grid view only) ═══ */}
            {!listView && providerFilter.length >= 2 && (
              <div className="p-2" data-testid="day-multi-provider-grid">
                <ProviderColumnGrid
                  testId="day-grid"
                  providers={visibleProviders
                    .filter(p => providerFilter.includes(p.id))
                    .map(p => ({
                      id: p.id,
                      name: p.name,
                      location: LOCATIONS.find(l => l.id === p.location)?.name,
                      color: p.color,
                      todayCount: filteredApts.filter(a => a.provider === p.id && a.status !== 'open').length,
                    }))}
                  appointments={filteredApts.map(a => ({ ...a, durationMin: a.durationMin || 30 }))}
                  hoursStart={8}
                  hoursEnd={17}
                  hourPx={HOUR_PX}
                  minColumnPx={200}
                  onAddSlot={(providerId, time) => { setNewApptPrefill({ time, provider: providerId }); setNewApptOpen(true); }}
                  renderBlock={(apt) => {
                    const st = STATUS_STYLE[apt.status] || STATUS_STYLE.scheduled;
                    const isOB = DEMO_OVERBOOKED_APT_IDS.has(apt.id);
                    const cx = contingencies[apt.id];
                    const provObj = ALL_PROVIDERS.find(p => p.id === apt.provider);
                    return (
                      <AppointmentHoverDetails
                        apt={apt}
                        providerName={provObj?.name}
                        providerColor={provObj?.color}
                        providers={visibleProviders.map(p => ({ id: p.id, name: p.name, color: p.color }))}
                        isOverbook={isOB}
                        testId={`hover-day-grid-${apt.id}`}
                        onQuickEdit={({ patchedApt }) => {
                          setAppointments((prev) => prev.map(a => a.id === patchedApt.id ? patchedApt : a));
                          const newProv = visibleProviders.find(p => p.id === patchedApt.provider);
                          toast.success(`Updated ${patchedApt.patient}`, { description: `${patchedApt.time} · ${newProv?.name || patchedApt.provider}${patchedApt.room ? ' · ' + patchedApt.room : ''}` });
                        }}
                      >
                        <div
                          className="h-full w-full px-2 py-1.5 rounded-md cursor-pointer relative overflow-hidden"
                          style={{
                            background: isOB ? 'repeating-linear-gradient(135deg, #fffbeb, #fffbeb 6px, #fef3c7 6px, #fef3c7 12px)' : st.bg,
                            borderLeft: `3px solid ${isOB ? '#f59e0b' : st.border}`,
                            boxShadow: isOB ? '0 0 0 1.5px #f59e0b' : 'none',
                          }}
                          onClick={() => openLightbox(apt)}
                          data-testid={`day-grid-apt-${apt.id}`}
                        >
                          {isOB && (
                            <span className="absolute top-1 left-1" data-testid={`ob-stack-icon-grid-${apt.id}`} title="Overbook active">
                              <Layers className="h-2.5 w-2.5" style={{ color: '#111' }} />
                            </span>
                          )}
                          <div className="flex items-start gap-1 flex-wrap">
                            <span className="text-[11px] font-medium text-slate-800 truncate">{apt.patient}</span>
                            {isOB && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setOverbookApt(apt); }}
                                className="text-[8px] font-bold px-1.5 py-0.5 rounded-md bg-amber-500 text-white inline-flex items-center gap-0.5"
                                style={{ border: 'none', cursor: 'pointer' }}
                                data-testid={`ob-badge-grid-${apt.id}`}
                              >
                                <AlertTriangle className="h-2 w-2" />
                                OB
                              </button>
                            )}
                          </div>
                          <div className="text-[9px] text-slate-500 truncate">{apt.type}</div>
                          {st.label && <div className="text-[9px] text-emerald-600">{st.border === '#22c55e' ? '✓' : '⚠'} {st.label}</div>}
                          {isOB && cx && (
                            <div className="text-[8px] mt-0.5 text-slate-600">
                              Contingency: <b>{cx.waitlistPatient}</b> · {cx.arrivalProbability}%
                            </div>
                          )}
                        </div>
                      </AppointmentHoverDetails>
                    );
                  }}
                  renderOpenSlot={(apt) => (
                    <div
                      className="h-full w-full px-2 py-1.5 rounded-md border border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 cursor-pointer"
                      onClick={() => { setNewApptPrefill({ time: apt.time }); setNewApptOpen(true); }}
                      data-testid={`day-grid-open-${apt.id}`}
                    >
                      <div className="text-[9px] text-slate-400 italic">Available</div>
                      <Badge className="text-[8px] border-0 bg-slate-100 text-slate-700 gap-0.5 mt-0.5">
                        <Sparkles className="h-2 w-2" /> 3 matches
                      </Badge>
                    </div>
                  )}
                />
              </div>
            )}
            {/* ═══ SINGLE-COLUMN TIMELINE — solo provider view (1 provider, grid view only) ═══ */}
            {!listView && providerFilter.length < 2 && HOURS.map((hour) => {
              const hourApts = filteredApts.filter(a => a.time.startsWith(hour.split(':')[0]));
              return (
                <div key={hour} className="flex min-h-[56px] border-b border-slate-50 last:border-0 group">
                  <div className="w-16 py-2 px-3 text-[10px] text-slate-400 font-medium shrink-0 border-r border-slate-50">{formatHour(hour)}</div>
                  <div className="flex-1 py-1 px-2 space-y-1">
                    {hourApts.map(apt => {
                      const st = STATUS_STYLE[apt.status] || STATUS_STYLE.scheduled;
                      const prov = ALL_PROVIDERS.find(p => p.id === apt.provider);
                      if (apt.status === 'open') {
                        return (
                          <div key={apt.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-dashed border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition-colors group/slot cursor-pointer"
                            onClick={() => onBook?.({ time: apt.time })} data-testid={`open-slot-${apt.id}`}>
                            <span className="text-[10px] text-slate-400 italic">{formatHour(apt.time)} — Available</span>
                            <div className="flex items-center gap-2 opacity-0 group-hover/slot:opacity-100 transition-opacity">
                              <Badge className="text-[8px] border-0 bg-violet-100 text-violet-600 gap-0.5"><Sparkles className="h-2 w-2" />3 matches</Badge>
                              <span className="text-[8px] text-slate-400">~$150</span>
                              <Button size="sm" className="h-5 text-[9px] bg-[#111] hover:bg-[#222] px-2" onClick={(e) => { e.stopPropagation(); onBook?.({ time: apt.time }); }}>+ Add</Button>
                              <Button size="sm" className="h-5 text-[9px] bg-[#6b5ce7] hover:bg-[#5a4bd6] px-2 gap-0.5" onClick={(e) => { e.stopPropagation(); toast.success('Echo AI auto-filling slot...'); }}>
                                <Sparkles className="h-2 w-2" />Auto-fill
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      const isOB = DEMO_OVERBOOKED_APT_IDS.has(apt.id);
                      const cx = contingencies[apt.id];
                      const ctxOpen = ctxMenuId === apt.id;
                      return (
                        <div key={apt.id}
                          className={cn(
                            "px-3 py-2 rounded-lg cursor-pointer hover:opacity-95 transition-opacity relative"
                          )}
                          style={{
                            background: isOB ? 'repeating-linear-gradient(135deg, #fffbeb, #fffbeb 6px, #fef3c7 6px, #fef3c7 12px)' : st.bg,
                            borderLeft: `3px solid ${isOB ? '#f59e0b' : st.border}`,
                            boxShadow: isOB ? '0 0 0 1.5px #f59e0b' : 'none',
                            // When ctx-menu is open, lift the entire row so its
                            // absolute-positioned dropdown stacks above any later
                            // sibling rows (e.g. open-slot with Auto-fill button).
                            zIndex: ctxOpen ? 50 : 'auto',
                          }}
                          onClick={() => openLightbox(apt)}
                          data-testid={`cal-apt-${apt.id}`}
                        >
                          {/* Stacked-card OB indicator (spec: 10px #6b5ce7 top-left) */}
                          {isOB && (
                            <span
                              className="absolute top-1 left-1"
                              data-testid={`ob-stack-icon-${apt.id}`}
                              title={cx ? `Overbook active · ${cx.waitlistPatient} contingency hold` : 'Overbook active'}
                            >
                              <Layers className="h-2.5 w-2.5" style={{ color: '#6b5ce7' }} />
                            </span>
                          )}
                          <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[11px] font-medium text-slate-800">{apt.patient}</span>
                                {apt.room && <Badge variant="outline" className="text-[8px] gap-0.5"><MapPin className="h-2 w-2" />{apt.room}</Badge>}
                                {apt.copay && <Badge variant="outline" className="text-[8px] border-amber-200 text-amber-600">${apt.copay} due</Badge>}
                                {isOB && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setOverbookApt(apt); }}
                                    className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-amber-500 text-white inline-flex items-center gap-1 shadow-sm hover:bg-amber-600 transition-colors"
                                    style={{ border: 'none', cursor: 'pointer' }}
                                    data-testid={`ob-badge-${apt.id}`}
                                    title="Click to review overbooking"
                                  >
                                    <AlertTriangle className="h-2.5 w-2.5" />
                                    OVERBOOKED
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-[9px] text-slate-400">{apt.type} · {prov?.name}</span>
                                {st.label && <span className={cn("text-[8px] font-medium", st.border === '#22c55e' ? 'text-emerald-600' : 'text-red-600')}>{st.border === '#22c55e' ? '✓' : '⚠'} {st.label}</span>}
                                {apt.noShow > 30 && !st.label && <span className="text-[8px] text-red-600">⚠ {apt.noShow}% no-show risk</span>}
                                {isOB && cx && <span className="text-[8px] text-amber-700 font-semibold">· {cx.riskScore}% risk · Echo OB active</span>}
                              </div>
                            </div>
                            {prov && <div className="h-2 w-2 rounded-full shrink-0" style={{ background: prov.color }} />}
                            {/* 3-dot context menu (spec §Smart Overbook Toggle) */}
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => setCtxMenuId(ctxMenuId === apt.id ? null : apt.id)}
                                className="p-0.5 rounded hover:bg-slate-200/60"
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                data-testid={`apt-ctx-btn-${apt.id}`}
                                aria-label="Appointment actions"
                              >
                                <MoreVertical className="h-3 w-3 text-slate-500" />
                              </button>
                              {ctxMenuId === apt.id && (
                                <div
                                  className="absolute right-0 mt-1 w-48 rounded-md border border-slate-200 bg-white shadow-lg py-1"
                                  style={{ zIndex: 60 }}
                                  role="menu"
                                  data-testid={`apt-ctx-menu-${apt.id}`}
                                >
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setCtxMenuId(null); toast.info('Slot manually flagged as high risk'); }}
                                    className="w-full text-left px-3 py-1.5 text-[10px] text-slate-700 hover:bg-slate-50"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                    data-testid={`apt-ctx-mark-${apt.id}`}
                                  >
                                    Mark as high risk
                                  </button>
                                  {!isOB && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCtxMenuId(null);
                                        toast.success('Contingency offer queued — Echo contacting best waitlist match');
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-[10px] text-slate-700 hover:bg-slate-50"
                                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                      data-testid={`apt-ctx-add-${apt.id}`}
                                    >
                                      Add overbook contingency
                                    </button>
                                  )}
                                  {isOB && cx && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCtxMenuId(null);
                                        setContingencies((prev) => {
                                          const next = { ...prev };
                                          delete next[apt.id];
                                          return next;
                                        });
                                        toast.info(`Contingency released — ${cx.waitlistPatient} back on the waitlist`);
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-[10px] text-slate-700 hover:bg-slate-50"
                                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                      data-testid={`apt-ctx-release-${apt.id}`}
                                    >
                                      Release contingency
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRiskApt(apt);
                                      // Defer ctx-menu close so the Dialog has a
                                      // stable parent tree when mounting. Closing
                                      // synchronously caused intermittent mount
                                      // failures on higher-positioned cards.
                                      setTimeout(() => setCtxMenuId(null), 0);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-[10px] text-slate-700 hover:bg-slate-50"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                    data-testid={`apt-ctx-risk-${apt.id}`}
                                  >
                                    View risk breakdown
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Contingency line inside block (spec §Overbooking Slot Indicator) */}
                          {isOB && cx && (
                            <div
                              className="mt-1.5 pt-1.5 border-t border-amber-200/80 space-y-0.5"
                              data-testid={`contingency-line-${apt.id}`}
                            >
                              <div className="text-[9px]" style={{ color: '#6b5ce7' }}>
                                Contingency: <b>{cx.waitlistPatient}</b> · {cx.arrivalProbability}% arrival probability
                              </div>
                              <div
                                className={cn(
                                  'text-[9px] font-medium',
                                  cx.status === 'confirmed' ? 'text-emerald-600'
                                    : cx.status === 'declined' ? 'text-red-600'
                                    : 'text-amber-700'
                                )}
                              >
                                Status: {cx.aiActiveLabel}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {hourApts.length === 0 && (
                      <div className="h-full min-h-[36px] rounded-lg hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => onBook?.({ time: hour })} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right rail removed — Financial Summary, No-show, Slot cards moved to left rail per user request.
              Schedule AI chat is now in the floating bottom-right assistant. */}
        </div>
      )}

      {/* ═══ WEEK VIEW (detailed time-grid, clickable day headers) ═══ */}
      {calView === 'week' && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex" data-testid="week-view">
          {/* Shared day headers + time-grid for each day */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Header row */}
            <div className="grid border-b border-slate-200" style={{ gridTemplateColumns: `${GUTTER_WIDTH}px repeat(7, 1fr)` }} data-testid="week-header-row">
              <div className="border-r border-slate-100 bg-slate-50/60" />
              {WEEK_DATA.map((d) => {
                const ob = DEMO_WEEK_OVERBOOK_BY_DATE[d.date];
                const obOver = ob && ob.used > ob.limit;
                const obWarn = ob && ob.used === ob.limit;
                const isSelected = d.date === selectedDate;
                return (
                  <button
                    key={d.day}
                    onClick={() => { setSelectedDate(d.date); setCalView('day'); }}
                    className={cn(
                      "p-2 text-center border-r border-slate-100 last:border-0 transition-colors text-left",
                      isSelected ? "bg-slate-100" : d.isToday ? "bg-slate-50/80" : "bg-white hover:bg-slate-50"
                    )}
                    style={{ border: 'none', borderRight: '1px solid #f1f5f9', cursor: 'pointer' }}
                    data-testid={`week-day-header-${d.date}`}
                    title="Click to open day view"
                  >
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">{d.day}</p>
                    <p className={cn("text-lg font-bold leading-none", isSelected ? "text-[#111]" : d.isToday ? "text-slate-900" : "text-slate-700")}>{d.date}</p>
                    <p className="text-[9px] text-slate-500 mt-0.5">{d.count} appts · {d.revenue}</p>
                    {ob && (
                      <div
                        className={cn(
                          "mt-1 inline-block px-1.5 py-0.5 rounded-full text-[8px] font-semibold",
                          obOver ? "bg-red-50 text-red-700"
                            : obWarn ? "bg-amber-50 text-amber-700"
                            : "bg-slate-50 text-slate-500"
                        )}
                        data-testid={`ob-counter-${d.date}`}
                      >
                        {ob.used}/{ob.limit} OB
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Scrollable time grid body */}
            <div ref={weekScrollRef} className="flex-1 overflow-y-auto relative" style={{ maxHeight: 620 }} data-testid="week-time-grid">
              {/* Current time line (overlay across selected week if today is within) */}
              {WEEK_DATA.some(d => d.isToday) && (
                <CurrentTimeLine
                  dayStartHour={DAY_START_HOUR}
                  dayEndHour={DAY_END_HOUR}
                  gutterWidth={GUTTER_WIDTH}
                  hourPx={HOUR_PX}
                  mode="week"
                  testId="calendar-current-time-week"
                />
              )}
              {HOURS.map((hour) => (
                <div key={hour} className="grid border-b border-slate-50" style={{ gridTemplateColumns: `${GUTTER_WIDTH}px repeat(7, 1fr)`, minHeight: HOUR_PX }}>
                  <div className="text-[9px] text-slate-400 text-right pr-2 pt-0.5 border-r border-slate-100 bg-slate-50/40">{formatHour(hour)}</div>
                  {WEEK_DATA.map((d) => {
                    // Only the current "today" day in the demo has real appointments;
                    // other days show lightweight bucket placeholders.
                    const [hh] = hour.split(':'); const h = parseInt(hh);
                    const half = `${h}:30`;
                    const slotApts = d.isToday
                      ? filteredApts.filter((a) => {
                          const [ah, am] = a.time.split(':');
                          return parseInt(ah) === h && (am === '00' || am === '30' || a.time === hour || a.time === half);
                        })
                      : [];
                    const hasMock = !d.isToday && d.count > 0 && (h === 9 + ((d.date) % 3) || h === 11 + ((d.date) % 2));
                    return (
                      <div
                        key={d.date}
                        className={cn("border-r border-slate-50 last:border-0 relative p-0.5 space-y-0.5", d.isToday && "bg-slate-50/20")}
                        onClick={() => {
                          if (slotApts.length === 0 && !hasMock) {
                            setSelectedDate(d.date); setCalView('day');
                          }
                        }}
                        style={{ cursor: slotApts.length === 0 && !hasMock ? 'pointer' : 'default' }}
                        data-testid={`week-slot-${d.date}-${h}`}
                      >
                        {slotApts.map((apt) => {
                          const st = STATUS_STYLE[apt.status] || STATUS_STYLE.scheduled;
                          const isOB = DEMO_OVERBOOKED_APT_IDS.has(apt.id);
                          return (
                            <button
                              key={apt.id}
                              onClick={(e) => { e.stopPropagation(); openLightbox(apt); }}
                              className="block w-full text-left px-1.5 py-0.5 rounded-sm truncate hover:ring-1 hover:ring-slate-300"
                              style={{
                                background: isOB ? 'repeating-linear-gradient(135deg,#fffbeb,#fffbeb 4px,#fef3c7 4px,#fef3c7 8px)' : st.bg,
                                borderLeft: `2px solid ${isOB ? '#f59e0b' : st.border}`,
                                border: 'none',
                                borderLeftWidth: '2px',
                                borderLeftColor: isOB ? '#f59e0b' : st.border,
                                cursor: 'pointer',
                              }}
                              title={`${apt.patient} · ${apt.time} · ${apt.type}`}
                              data-testid={`week-apt-${apt.id}`}
                            >
                              <span className="text-[9px] font-semibold text-slate-800 truncate block">{apt.patient}</span>
                              <span className="text-[8px] text-slate-500 truncate block">{apt.type}</span>
                            </button>
                          );
                        })}
                        {hasMock && (
                          <div className="px-1.5 py-0.5 rounded-sm bg-slate-50 border-l-2 border-slate-200 truncate">
                            <span className="text-[9px] text-slate-500 truncate block">Follow-up</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ MONTH VIEW ═══ */}
      {calView === 'month' && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="month-view">
          <div className="grid grid-cols-7 border-b border-slate-100">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="py-2 text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{d}</div>
            ))}
          </div>
          {MONTH_WEEKS.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b border-slate-50 last:border-0">
              {week.map((day, di) => {
                const isToday = day === today;
                const aptCount = day ? (day === 17 ? 8 : day === 18 ? 5 : day === 14 ? 7 : day === 15 ? 9 : day === 16 ? 6 : day > 0 ? Math.floor(Math.random() * 6) + 2 : 0) : 0;
                const statusColor = day === 17 ? '#f59e0b' : day === 18 ? '#ef4444' : aptCount > 0 ? '#22c55e' : '#d1d5db';
                return (
                  <div key={di} className={cn("min-h-[70px] p-2 border-r border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer", isToday && "bg-blue-50/30")}
                    onClick={() => day && setCalView('day')}>
                    {day && (
                      <>
                        <p className={cn("text-xs font-medium", isToday ? "text-blue-600" : "text-slate-700")}>{day}</p>
                        {aptCount > 0 && (
                          <>
                            <p className="text-[9px] text-slate-400 mt-0.5">{aptCount} appts</p>
                            <div className="h-1 rounded-full mt-1" style={{ background: statusColor, width: '60%' }} />
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* ═══ APPOINTMENT LIGHTBOX ═══ */}
      <Dialog open={!!lightboxApt} onOpenChange={(v) => { if (!v) setLightboxApt(null); }}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="apt-lightbox">
          <DialogHeader className="sr-only">
            <DialogTitle>{lightboxApt?.patient || 'Appointment details'}</DialogTitle>
            <DialogDescription>Appointment and clinical snapshot</DialogDescription>
          </DialogHeader>
          {lightboxApt && (() => {
            const det = PATIENT_DETAILS[lightboxApt.id] || {};
            const prov = ALL_PROVIDERS.find(p => p.id === lightboxApt.provider);
            const st = STATUS_STYLE[lightboxApt.status] || STATUS_STYLE.scheduled;
            return (
              <>
                {/* Header strip */}
                <div className="px-5 py-3 flex items-center gap-3" style={{ borderBottom: '1px solid #f0f0f0', borderLeft: `4px solid ${st.border}` }}>
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-semibold text-slate-900">{lightboxApt.patient}</p>
                    <p className="text-[10px] text-slate-400">{det.mrn} · DOB: {det.dob}</p>
                  </div>
                  <Badge className={cn("text-[9px] border", lightboxApt.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : lightboxApt.status === 'flagged' ? 'bg-red-50 text-red-700 border-red-200' : lightboxApt.status === 'at_risk' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-100 text-slate-600 border-slate-200')}>
                    {lightboxApt.status === 'confirmed' ? 'Confirmed' : lightboxApt.status === 'flagged' ? 'Flagged' : lightboxApt.status === 'at_risk' ? 'At Risk' : 'Scheduled'}
                  </Badge>
                </div>
                {/* Appointment details */}
                <div className="px-5 py-3 grid grid-cols-3 gap-3 border-b border-slate-100">
                  <div><p className="text-[9px] text-slate-400 uppercase">Time</p><p className="text-xs font-medium text-slate-800"><Clock className="h-3 w-3 inline mr-1" />{formatHour(lightboxApt.time)}</p></div>
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase">Type</p>
                    <p className="text-xs font-medium text-slate-800 flex items-center gap-1">
                      {lightboxApt.type}
                      {(() => {
                        const vt = DEMO_VISIT_TYPES_CATALOG.find(v => v.name === lightboxApt.type);
                        const flagBadges = [];
                        if (vt?.flags?.nonBillable) flagBadges.push({ label: 'Non-billable', color: 'amber' });
                        if (vt?.flags?.teleHealth) flagBadges.push({ label: 'Telehealth', color: 'blue' });
                        if (vt?.flags?.referringRequired) flagBadges.push({ label: 'Referring MD', color: 'violet' });
                        return flagBadges.map((b, i) => (
                          <Badge key={i} className={cn('text-[8px] border-0', b.color === 'amber' ? 'bg-amber-50 text-amber-700' : b.color === 'blue' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700')}>
                            {b.label}
                          </Badge>
                        ));
                      })()}
                    </p>
                  </div>
                  <div><p className="text-[9px] text-slate-400 uppercase">Provider</p><p className="text-xs font-medium text-slate-800"><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: prov?.color }} />{prov?.name}</p></div>
                  {lightboxApt.room && <div><p className="text-[9px] text-slate-400 uppercase">Room</p><p className="text-xs font-medium text-slate-800"><MapPin className="h-3 w-3 inline mr-1" />{lightboxApt.room}</p></div>}
                  <div><p className="text-[9px] text-slate-400 uppercase">Phone</p><p className="text-xs font-medium text-slate-800">{det.phone}</p></div>
                  <div><p className="text-[9px] text-slate-400 uppercase">Insurance</p><p className={cn("text-xs font-medium", det.insStatus === 'Active' ? 'text-emerald-600' : 'text-red-600')}><Shield className="h-3 w-3 inline mr-1" />{det.insurance} ({det.insStatus})</p></div>
                </div>
                {/* Clinical snapshot */}
                <div className="px-5 py-3 space-y-2 border-b border-slate-100">
                  <p className="text-[9px] text-slate-400 uppercase font-semibold">Clinical Snapshot</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 rounded-lg bg-slate-50">
                      <p className="text-[8px] text-slate-400 uppercase">Problems</p>
                      {(det.problems || []).length > 0
                        ? det.problems.map((p, i) => <p key={i} className="text-[10px] text-slate-700">{p}</p>)
                        : <p className="text-[10px] text-slate-400">None</p>}
                    </div>
                    <div className="p-2 rounded-lg bg-slate-50">
                      <p className="text-[8px] text-slate-400 uppercase">Medications</p>
                      {(det.meds || []).length > 0
                        ? det.meds.map((m, i) => <p key={i} className="text-[10px] text-slate-700">{m}</p>)
                        : <p className="text-[10px] text-slate-400">None</p>}
                    </div>
                    <div className="p-2 rounded-lg bg-slate-50">
                      <p className="text-[8px] text-slate-400 uppercase">Allergies</p>
                      {(det.allergies || []).length > 0
                        ? det.allergies.map((a, i) => <Badge key={i} className="text-[8px] border-0 bg-red-50 text-red-600 mr-1">{a}</Badge>)
                        : <p className="text-[10px] text-slate-400">NKDA</p>}
                    </div>
                  </div>
                  {lightboxApt.noShow > 20 && (
                    <div className="flex items-center gap-1.5 p-2 rounded-lg bg-red-50">
                      <AlertTriangle className="h-3 w-3 text-red-500" />
                      <span className="text-[10px] text-red-600 font-medium">{lightboxApt.noShow}% no-show risk</span>
                    </div>
                  )}
                  {lightboxApt.copay && (
                    <div className="flex items-center gap-1.5 p-2 rounded-lg bg-amber-50">
                      <DollarSign className="h-3 w-3 text-amber-500" />
                      <span className="text-[10px] text-amber-600 font-medium">${lightboxApt.copay} copay due</span>
                    </div>
                  )}
                </div>
                {/* Actions */}
                <DialogFooter className="px-5 py-3 gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => { setLightboxApt(null); navigate(`/echo/patients/${lightboxApt.id}`); }} data-testid="lightbox-open-profile">
                    <User className="h-3 w-3" /> Full Patient Profile
                  </Button>
                  <Button size="sm" className="text-xs gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={() => { setLightboxApt(null); window.open('/lumina/clinical-focus?patientId=' + (det.mrn || lightboxApt.id), '_blank'); }} data-testid="lightbox-open-lumina">
                    <Stethoscope className="h-3 w-3" /> Open Clinical Chart (Lumina)
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => { toast.info(`Calling ${det.phone}...`); }} data-testid="lightbox-call">
                    <Phone className="h-3 w-3" /> Call
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ═══ OVERBOOKING REVIEW DIALOG (demo-only) ═══ */}
      <Dialog open={!!overbookApt} onOpenChange={(v) => { if (!v) setOverbookApt(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="ob-approval-dialog">
          {overbookApt && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Overbooking review
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="text-slate-700">
                  <span className="font-medium">{overbookApt.patient}</span> is overbooked for{' '}
                  <span className="font-medium">
                    {ALL_PROVIDERS.find(p => p.id === overbookApt.provider)?.name}
                  </span>{' '}
                  at {formatHour(overbookApt.time)}.
                </p>
                <div className="rounded-md bg-amber-50 border border-amber-100 p-3 text-[12px] text-amber-800">
                  <div className="flex items-center gap-1.5 font-medium">
                    <Sparkles className="h-3.5 w-3.5" />
                    Echo AI recommendation
                  </div>
                  <p className="mt-1">
                    Net capacity likely unchanged — 2 of 3 currently overbooked patients have low
                    show probability. Approving this booking is expected to have minimal schedule impact.
                  </p>
                </div>
              </div>
              <DialogFooter className="gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => { toast.info('Overbooking declined'); setOverbookApt(null); }}
                  data-testid="ob-decline"
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  className="text-xs bg-[#111] hover:bg-[#222]"
                  onClick={() => { toast.success('Overbooking approved'); setOverbookApt(null); }}
                  data-testid="ob-approve"
                >
                  Approve
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ RISK BREAKDOWN PANEL (spec §View risk breakdown) ═══ */}
      <RiskBreakdownPanel
        open={!!riskApt}
        onOpenChange={(v) => { if (!v) setRiskApt(null); }}
        patient={riskApt?.patient || ''}
        time={riskApt ? formatHour(riskApt.time) : ''}
        provider={riskApt ? ALL_PROVIDERS.find(p => p.id === riskApt.provider)?.name || '' : ''}
        score={riskApt ? (contingencies[riskApt.id]?.riskScore || riskApt.noShow || 0) : 0}
        factors={riskApt ? (contingencies[riskApt.id]?.riskFactors || []) : []}
        matches={3}
        onOverbook={() => {
          if (!riskApt) return;
          setContingencies((prev) => ({
            ...prev,
            [riskApt.id]: prev[riskApt.id] || {
              waitlistPatient: 'Next waitlist match',
              arrivalProbability: 85,
              status: 'offer_sent',
              aiActiveLabel: 'Offer sent — awaiting reply',
              riskScore: riskApt.noShow || 50,
              riskFactors: [],
            },
          }));
          toast.success('Contingency offer queued');
        }}
      />

      {/* ═══ NEW APPOINTMENT LIGHTBOX (v2 — from multi-provider grid slot-add) ═══ */}
      <NewAppointmentLightbox
        open={newApptOpen}
        onOpenChange={setNewApptOpen}
        prefillTime={newApptPrefill?.time}
        patients={DEMO_LIGHTBOX_PATIENTS}
        locations={DEMO_LIGHTBOX_LOCATIONS}
        providers={ALL_PROVIDERS.filter(p => providerFilter.includes(p.id)).map(p => p.name)}
        visitTypes={DEMO_VISIT_TYPES_CATALOG}
        openSlots={DEMO_LIGHTBOX_OPEN_SLOTS}
        onConfirm={({ patient, selection }) => {
          const vt = DEMO_VISIT_TYPES_CATALOG.find(v => v.id === selection.visitTypeId);
          toast.success(`Booked ${patient?.name}`, {
            description: `${vt?.name || 'Visit'} · ${selection.slot?.time} · ${selection.provider}`,
          });
          setNewApptPrefill(null);
        }}
      />
    </div>
  );
};

export default SchedulerCalendar;
